import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, CircularProgress, Skeleton, alpha, Tooltip, Chip,
  IconButton, Drawer, Divider, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from '@mui/material';
import { Close, Assessment, FileDownloadOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, LineChart, Line, ReferenceLine,
} from 'recharts';
import { distributionAPI } from '../../services/api';
import {
  REGIME_COLORS, LEVEL_META, EmptyState, LevelBadge, SignalChips,
  actionColor, exportDrawerCapture, fmtBn, fmtPct, fmtScore,
  getDrawerActionSummary, getSectorLabel, getTimelineDomain,
  getTimelineTicks, levelColor, thSx, tdSx,
} from './shared';

const TickerDrawer = ({ open, onClose, ticker, companyName, tickerMeta = null }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [sectorCtx, setSectorCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportingImage, setExportingImage] = useState(false);
  const drawerCaptureRef = useRef(null);
  const drawerScrollRef = useRef(null);

  useEffect(() => {
    if (!open || !ticker) return;
    setLoading(true);
    Promise.all([
      distributionAPI.getTicker(ticker),
      distributionAPI.getTickerSectorContext(ticker).catch(() => ({ data: null })),
    ]).then(([response, sectorResponse]) => {
      setData(response.data);
      setSectorCtx(sectorResponse.data?.sector_context || null);
    }).catch(() => {
      setData(null);
      setSectorCtx(null);
    }).finally(() => setLoading(false));
  }, [open, ticker]);

  const history = data?.data || [];
  const chartData = [...history].reverse().map((item, index) => ({
    x: index,
    date: item.date?.slice(5),
    score: item.total_dist_score,
    level: item.dist_level,
    signals: item.active_signal_count,
  }));
  const chartTicks = getTimelineTicks(chartData);
  const chartDomain = getTimelineDomain(chartData);

  const latest = history.length > 0 ? history[0] : null;
  const resolvedCompanyName = companyName || data?.company_name || latest?.company_name || ticker;
  const sectorLabel = getSectorLabel(sectorCtx, i18n.language)
    || getSectorLabel(tickerMeta, i18n.language)
    || getSectorLabel(data, i18n.language)
    || getSectorLabel(latest, i18n.language)
    || '—';
  const actionSummary = getDrawerActionSummary(latest, sectorCtx, t);

  const handleExportDrawerImage = useCallback(async () => {
    if (!drawerCaptureRef.current || !drawerScrollRef.current || !latest || exportingImage) return;

    try {
      setExportingImage(true);
      const asOfDate = latest.date || new Date().toISOString().slice(0, 10);
      await exportDrawerCapture({
        captureNode: drawerCaptureRef.current,
        scrollNode: drawerScrollRef.current,
        fileName: `${ticker || 'distribution'}-drawer-${asOfDate}.png`,
      });
    } catch (error) {
      console.error('Failed to export ticker drawer image', error);
    } finally {
      setExportingImage(false);
    }
  }, [exportingImage, latest, ticker]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 440, md: 480 }, bgcolor: '#fafbfc' } }}>
      <Box ref={drawerCaptureRef} sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: '#fafbfc' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: 1.5, bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
              <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.9rem' }}>{ticker}</Typography>
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#1e293b', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={resolvedCompanyName}>
                  {resolvedCompanyName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, minWidth: 0, flexWrap: 'wrap' }}>
                {sectorLabel !== '—' ? (
                  <Box sx={{ flexShrink: 0, px: 0.7, py: 0.2, borderRadius: '6px', bgcolor: alpha('#0ea5e9', 0.1), color: '#0369a1', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '-0.005em', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                    {sectorLabel}
                  </Box>
                ) : null}
                {latest ? <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', flexShrink: 0 }}>{latest.date}</Typography> : null}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }} data-html2canvas-ignore="true" data-export-header-toolbar="true">
            <Tooltip title={exportingImage ? t('common.exportingImage', 'Đang xuất ảnh...') : t('common.exportImage', 'Xuất ảnh')} arrow>
              <span>
                <IconButton
                  onClick={handleExportDrawerImage}
                  disabled={!latest || loading || exportingImage}
                  size="small"
                  sx={{
                    border: '1px solid #cbd5e1',
                    bgcolor: '#fff',
                    color: '#475569',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                    '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
                  }}
                >
                  {exportingImage ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlined sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} size="small"><Close /></IconButton>
          </Box>
        </Box>
        <Divider />

        <Box ref={drawerScrollRef} sx={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Skeleton variant="rounded" width="100%" height={100} />
              <Skeleton variant="rounded" width="100%" height={200} />
            </Box>
          ) : !latest ? (
            <EmptyState title={t('distribution.noData')} />
          ) : (
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {actionSummary && (
                <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: `1px solid ${alpha(actionSummary.color, 0.22)}`, overflow: 'hidden' }}>
                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: alpha(actionSummary.color, 0.06), borderBottom: `1px solid ${alpha(actionSummary.color, 0.12)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#334155' }}>{t('distribution.drawer.summary.title')}</Typography>
                    <Chip label={actionSummary.actionLabel} size="small" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 800, bgcolor: alpha(actionSummary.color, 0.12), color: actionSummary.color }} />
                  </Box>
                  <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: actionSummary.color, lineHeight: 1.35 }}>{actionSummary.headline}</Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: '#475569', lineHeight: 1.65 }}>{actionSummary.rationale}</Typography>
                    {actionSummary.drivers.length > 0 && (
                      <Box>
                        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.45 }}>{t('distribution.drawer.summary.driversLabel')}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.45, flexWrap: 'wrap' }}>
                          {actionSummary.drivers.map((driver) => (
                            <Chip key={driver} label={driver} size="small" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha(actionSummary.color, 0.08), color: actionSummary.color }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                    <Box sx={{ borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', px: 1, py: 0.85 }}>
                      <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.35 }}>{t('distribution.drawer.summary.sectorLabel')}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: '#475569', lineHeight: 1.6 }}>{actionSummary.sectorNote}</Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography sx={{ fontSize: '1.8rem', fontWeight: 900, color: '#1e293b', fontFamily: 'monospace' }}>
                  {latest.close ? Number(latest.close).toLocaleString() : '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, fontFamily: 'monospace', color: latest.pct_change < 0 ? '#ef4444' : '#10b981' }}>
                  {fmtPct(latest.pct_change)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                  {[
                    { label: 'MA10', ok: latest.above_ma10 },
                    { label: 'MA20', ok: latest.above_ma20 },
                    { label: 'MA50', ok: latest.above_ma50 },
                  ].map(({ label, ok }) => (
                    <Typography key={label} sx={{ fontSize: '0.62rem', fontWeight: 700, color: ok ? '#10b981' : '#ef4444', bgcolor: ok ? alpha('#10b981', 0.08) : alpha('#ef4444', 0.08), px: 0.6, py: 0.2, borderRadius: 0.8 }}>
                      {ok ? '▲' : '▼'}{label}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {sectorCtx && (() => {
                const regime = sectorCtx.sector_regime || 'neutral';
                const regimeColor = REGIME_COLORS[regime] || '#64748b';
                const name = getSectorLabel(sectorCtx, i18n.language);
                return (
                  <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: `1px solid ${alpha(regimeColor, 0.2)}`, overflow: 'hidden' }}>
                    <Box sx={{ px: 1.5, py: 0.8, bgcolor: alpha(regimeColor, 0.04), borderBottom: `1px solid ${alpha(regimeColor, 0.1)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Assessment sx={{ fontSize: 14, color: regimeColor }} />
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>{t('distribution.sectorRegime.sectorContext')}</Typography>
                      <Chip label={name || sectorCtx.sector} size="small" sx={{ fontSize: '0.58rem', height: 18, fontWeight: 600, bgcolor: alpha(regimeColor, 0.08), color: regimeColor, ml: 'auto' }} />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', bgcolor: '#e2e8f0' }}>
                      {[
                        { label: t(`distribution.sectorRegime.regimes.${regime}`), value: sectorCtx.sector_score?.toFixed(0) || '—', color: regimeColor },
                        { label: t('distribution.sectorRegime.rankInSector'), value: `${sectorCtx.sector_rank_position || '—'}/${sectorCtx.sector_size || '—'}`, color: '#475569' },
                        { label: t('distribution.sectorRegime.strengthScore'), value: sectorCtx.stock_strength_score?.toFixed(1) || '—', color: '#3b82f6' },
                        { label: t('distribution.sectorRegime.rankScore'), value: sectorCtx.within_sector_rank_score?.toFixed(1) || '—', color: '#7c3aed' },
                        { label: t('distribution.sectorRegime.pressure'), value: sectorCtx.sector_pressure_label || '—', color: (sectorCtx.pct_breakdown || 0) >= 0.2 ? '#ef4444' : '#64748b' },
                        { label: t('distribution.sectorRegime.relativeStrength'), value: sectorCtx.sector_relative_label || '—', color: '#10b981' },
                        { label: t('distribution.sectorRegime.leaders'), value: `${((sectorCtx.pct_leaders || 0) * 100).toFixed(0)}%`, color: '#10b981' },
                        { label: t('distribution.sectorRegime.distributionRate'), value: `${((sectorCtx.pct_distribution || 0) * 100).toFixed(0)}%`, color: '#f97316' },
                        { label: t('distribution.sectorRegime.breakdown'), value: `${((sectorCtx.pct_breakdown || 0) * 100).toFixed(0)}%`, color: '#ef4444' },
                        { label: t('distribution.sectorRegime.actionHint'), value: sectorCtx.sector_action_hint || '—', color: regimeColor },
                        { label: t('distribution.sectorRegime.aboveMa20'), value: `${((sectorCtx.pct_above_ma20 || 0) * 100).toFixed(0)}%`, color: (sectorCtx.pct_above_ma20 || 0) >= 0.5 ? '#10b981' : '#ef4444' },
                        { label: t('distribution.sectorRegime.avgReturn'), value: fmtPct(sectorCtx.avg_ret_5), color: (sectorCtx.avg_ret_5 || 0) < 0 ? '#ef4444' : '#10b981' },
                        { label: t('distribution.sectorRegime.avgReturn10d'), value: fmtPct(sectorCtx.avg_ret_10), color: (sectorCtx.avg_ret_10 || 0) < 0 ? '#ef4444' : '#10b981' },
                      ].map(({ label, value, color }) => (
                        <Box key={label} sx={{ bgcolor: '#fff', px: 1, py: 0.8, textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color, fontFamily: 'monospace', lineHeight: 1.4 }}>{value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()}

              <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569' }}>{t('distribution.drawer.scoreBreakdown')}</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 900, color: levelColor(latest.dist_level), fontFamily: 'monospace' }}>
                    {fmtScore(latest.total_dist_score)}<span style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 500 }}>/14</span>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', height: 18, borderRadius: 1, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
                  <Tooltip title={`${t('distribution.drawer.signalScore')}: ${fmtScore(latest.dist_score)}`} arrow>
                    <Box sx={{ width: `${(latest.dist_score / 14) * 100}%`, bgcolor: '#3b82f6', transition: 'width 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {latest.dist_score >= 1 && <Typography sx={{ fontSize: '0.5rem', color: '#fff', fontWeight: 700 }}>{fmtScore(latest.dist_score)}</Typography>}
                    </Box>
                  </Tooltip>
                  <Tooltip title={`${t('distribution.drawer.patternScore')}: ${fmtScore(latest.pattern_score)}`} arrow>
                    <Box sx={{ width: `${(latest.pattern_score / 14) * 100}%`, bgcolor: '#7c3aed', transition: 'width 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {latest.pattern_score >= 1 && <Typography sx={{ fontSize: '0.5rem', color: '#fff', fontWeight: 700 }}>{fmtScore(latest.pattern_score)}</Typography>}
                    </Box>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                    <Typography sx={{ fontSize: '0.58rem', color: '#64748b' }}>{t('distribution.drawer.signalScore')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#7c3aed' }} />
                    <Typography sx={{ fontSize: '0.58rem', color: '#64748b' }}>{t('distribution.drawer.patternScore')}</Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                {[
                  { label: t('distribution.drawer.level'), value: t(`distribution.levels.${(LEVEL_META[latest.dist_level] || LEVEL_META[1]).key}`), color: levelColor(latest.dist_level) },
                  { label: t('distribution.drawer.action'), value: t(`distribution.actions.${latest.dist_action}`, latest.dist_action), color: actionColor(latest.dist_action) },
                  { label: t('distribution.drawer.distCount'), value: latest.dist_count ?? '—', color: (latest.dist_count || 0) >= 4 ? '#ef4444' : '#64748b' },
                  { label: t('distribution.drawer.signals'), value: latest.active_signal_count, color: latest.active_signal_count >= 4 ? '#ef4444' : '#3b82f6' },
                  { label: 'Patterns', value: latest.active_pattern_count, color: latest.active_pattern_count >= 2 ? '#ef4444' : '#7c3aed' },
                  { label: t('distribution.drawer.sessionVolume'), value: latest.volume ? Number(latest.volume).toLocaleString() : '—', color: '#475569' },
                  { label: t('distribution.drawer.volRatio'), value: latest.vol_ratio ? `×${latest.vol_ratio.toFixed(2)}` : '—', color: (latest.vol_ratio || 1) >= 1.5 ? '#ef4444' : '#64748b' },
                  { label: t('distribution.drawer.closePosition'), value: fmtPct(latest.close_pos), color: (latest.close_pos || 0) >= 0.6 ? '#10b981' : '#f59e0b' },
                  { label: t('distribution.drawer.avgLiquidity'), value: fmtBn(latest.value_ma20), color: '#475569' },
                  { label: t('distribution.drawer.rs5d'), value: fmtPct(latest.rs_5), color: latest.rs_5 < 0 ? '#ef4444' : '#10b981' },
                  { label: t('distribution.drawer.ret5d'), value: fmtPct(latest.ret_5), color: latest.ret_5 < 0 ? '#ef4444' : '#10b981' },
                  { label: t('distribution.drawer.ret10d'), value: fmtPct(latest.ret_10), color: latest.ret_10 < 0 ? '#ef4444' : '#10b981' },
                ].map(({ label, value, color }) => (
                  <Box key={label} sx={{ bgcolor: '#fff', px: 1.5, py: 1 }}>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 900, color, fontFamily: 'monospace', lineHeight: 1.3 }}>{value}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>{t('distribution.drawer.activeSignals')}</Typography>
                </Box>
                <Box sx={{ p: 1.5 }}>
                  <SignalChips row={latest} />
                </Box>
              </Box>

              {chartData.length > 1 && (
                <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>{t('distribution.drawer.scoreHistory')}</Typography>
                  </Box>
                  <Box sx={{ px: 1, py: 1, height: 210 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 8, right: 6, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis type="number" dataKey="x" domain={chartDomain} ticks={chartTicks} tickFormatter={(value) => chartData[Math.round(value)]?.date || ''} tick={{ fontSize: 10 }} tickMargin={6} padding={{ left: 0, right: 0 }} allowDecimals={false} />
                        <YAxis domain={[0, 'auto']} tick={{ fontSize: 10 }} width={34} />
                        <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <ReferenceLine y={1.5} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'Lv1', fontSize: 9 }} />
                        <ReferenceLine y={5.5} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: 'Lv2', fontSize: 9 }} />
                        <ReferenceLine y={7.5} stroke="#f97316" strokeDasharray="3 3" label={{ value: 'Lv3', fontSize: 9 }} />
                        <Line type="monotone" dataKey="score" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} name="Score" />
                        <Line type="monotone" dataKey="signals" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="Signals" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}

              <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>{t('distribution.drawer.history')} ({history.length} {t('distribution.drawer.sessions')})</Typography>
                </Box>
                <TableContainer>
                  <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...thSx, whiteSpace: 'nowrap' }}>{t('distribution.drawer.date')}</TableCell>
                        <TableCell sx={{ ...thSx, whiteSpace: 'nowrap' }} align="right">{t('distribution.drawer.score')}</TableCell>
                        <TableCell sx={{ ...thSx, whiteSpace: 'nowrap' }}>{t('distribution.drawer.level')}</TableCell>
                        <TableCell sx={{ ...thSx, whiteSpace: 'nowrap' }} align="center">{t('distribution.drawer.signals')}</TableCell>
                        <TableCell sx={{ ...thSx, whiteSpace: 'nowrap' }} align="right">KL</TableCell>
                        <TableCell sx={{ ...thSx, whiteSpace: 'nowrap' }} align="right">%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map((row, index) => (
                        <TableRow key={index} sx={{ '&:nth-of-type(even)': { bgcolor: '#fafbfc' } }}>
                          <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap' }}>{row.date?.slice(5)}</TableCell>
                          <TableCell sx={{ ...tdSx, fontWeight: 700, color: levelColor(row.dist_level), fontFamily: 'monospace', whiteSpace: 'nowrap' }} align="right">
                            {fmtScore(row.total_dist_score)}
                          </TableCell>
                          <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap' }}><LevelBadge level={row.dist_level} /></TableCell>
                          <TableCell sx={{ ...tdSx, fontFamily: 'monospace', whiteSpace: 'nowrap' }} align="center">{row.active_signal_count}</TableCell>
                          <TableCell sx={{ ...tdSx, fontFamily: 'monospace', fontSize: '0.7rem', whiteSpace: 'nowrap', color: (row.vol_ratio || 1) >= 1.5 ? '#ef4444' : '#64748b' }} align="right">
                            {row.vol_ratio ? `×${row.vol_ratio.toFixed(2)}` : '—'}
                          </TableCell>
                          <TableCell sx={{ ...tdSx, color: row.pct_change < 0 ? '#ef4444' : '#10b981', fontWeight: 600, fontFamily: 'monospace', whiteSpace: 'nowrap' }} align="right">
                            {fmtPct(row.pct_change)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default TickerDrawer;